import React from 'react';
import { MultiSelect, NumberInput, Select, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import {
  AMOUNT_FILTER_OPTIONS,
  DATE_FILTER_OPTIONS,
  METHOD_LABELS,
  STATUS_LABELS,
  type AmountFilterMode,
  type DateFilterMode,
} from '../monitoring-page.constants';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type MonitoringFiltersProps = Pick<
  UseMonitoringPage,
  | 'statusFilter' | 'setStatusFilter'
  | 'companyFilter' | 'setCompanyFilter'
  | 'sessionFilter' | 'setSessionFilter'
  | 'orderFilter' | 'setOrderFilter'
  | 'methodFilter' | 'setMethodFilter'
  | 'amountFilterMode' | 'setAmountFilterMode'
  | 'amountExact' | 'setAmountExact'
  | 'amountMin' | 'setAmountMin'
  | 'amountMax' | 'setAmountMax'
  | 'dateFilterMode' | 'setDateFilterMode'
  | 'exactDate' | 'setExactDate'
  | 'dateFrom' | 'setDateFrom'
  | 'dateTo' | 'setDateTo'
  | 'companyOptions' | 'sessionOptions' | 'orderOptions'
  | 'clearFilters'
>;

export const MonitoringFilters: React.FC<MonitoringFiltersProps> = ({
  statusFilter,
  setStatusFilter,
  companyFilter,
  setCompanyFilter,
  sessionFilter,
  setSessionFilter,
  orderFilter,
  setOrderFilter,
  methodFilter,
  setMethodFilter,
  amountFilterMode,
  setAmountFilterMode,
  amountExact,
  setAmountExact,
  amountMin,
  setAmountMin,
  amountMax,
  setAmountMax,
  dateFilterMode,
  setDateFilterMode,
  exactDate,
  setExactDate,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  companyOptions,
  sessionOptions,
  orderOptions,
  clearFilters,
}) => (
  <div className={classes.filtersCard}>
    <div className={classes.filtersGrid}>
      <div className={classes.fieldStack}>
        <MultiSelect
          label={'סטטוס'}
          placeholder={'בחר סטטוסים'}
          data={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          data-testid="monitoring-payments-status"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'חברה'}
          placeholder={'בחר חברות'}
          data={companyOptions}
          value={companyFilter}
          onChange={setCompanyFilter}
          searchable
          clearable
          data-testid="monitoring-payments-company"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'משמרת'}
          placeholder={'בחר משמרות'}
          data={sessionOptions}
          value={sessionFilter}
          onChange={setSessionFilter}
          searchable
          clearable
          data-testid="monitoring-payments-session"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'הזמנה'}
          placeholder={'בחר הזמנות'}
          data={orderOptions}
          value={orderFilter}
          onChange={setOrderFilter}
          searchable
          clearable
          data-testid="monitoring-payments-order"
        />
      </div>

      <div className={classes.fieldStack}>
        <MultiSelect
          label={'אופן תשלום'}
          placeholder={'בחר אופני תשלום'}
          data={Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label }))}
          value={methodFilter}
          onChange={setMethodFilter}
          clearable
          data-testid="monitoring-payments-method"
        />
      </div>

      <div className={classes.fieldStack}>
        <Select
          label={'סינון סכום'}
          data={AMOUNT_FILTER_OPTIONS}
          value={amountFilterMode}
          onChange={(value) => setAmountFilterMode((value as AmountFilterMode) ?? 'all')}
          allowDeselect={false}
          data-testid="monitoring-payments-amount-mode"
        />
        {amountFilterMode === 'exact' && (
          <NumberInput value={amountExact} onChange={setAmountExact} placeholder={'סכום'} decimalScale={1} data-testid="monitoring-payments-amount-exact" />
        )}
        {amountFilterMode === 'between' && (
          <div className={classes.fieldRow}>
            <NumberInput value={amountMin} onChange={setAmountMin} placeholder={'מינימום'} decimalScale={1} data-testid="monitoring-payments-amount-min" />
            <NumberInput value={amountMax} onChange={setAmountMax} placeholder={'מקסימום'} decimalScale={1} data-testid="monitoring-payments-amount-max" />
          </div>
        )}
      </div>

      <div className={classes.fieldStack}>
        <Select
          label={'סינון תאריך'}
          data={DATE_FILTER_OPTIONS}
          value={dateFilterMode}
          onChange={(value) => setDateFilterMode((value as DateFilterMode) ?? 'all')}
          allowDeselect={false}
          data-testid="monitoring-payments-date-mode"
        />
        {dateFilterMode === 'exact' && (
          <TextInput type="date" value={exactDate} onChange={(event) => setExactDate(event.currentTarget.value)} data-testid="monitoring-payments-date-exact" />
        )}
        {dateFilterMode === 'between' && (
          <div className={classes.fieldRow}>
            <TextInput type="date" value={dateFrom} onChange={(event) => setDateFrom(event.currentTarget.value)} data-testid="monitoring-payments-date-from" />
            <TextInput type="date" value={dateTo} onChange={(event) => setDateTo(event.currentTarget.value)} data-testid="monitoring-payments-date-to" />
          </div>
        )}
      </div>
    </div>

    <div className={classes.filtersActions}>
      <Button variant="cancel" onClick={clearFilters} dataTestId="monitoring-payments-clear">
        {'נקה סינונים'}
      </Button>
    </div>
  </div>
);
